import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Mandala, Paisley, HennaHand } from "../HennaMotif";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.5 + i * 0.16, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pt-24"
    >
      {/* rotating mandala backdrop */}
      <Mandala className="pointer-events-none absolute -right-40 top-1/2 h-[70vh] w-[70vh] -translate-y-1/2 animate-spin-slow text-henna/25" />
      <Paisley className="pointer-events-none absolute left-6 top-28 hidden h-40 w-32 text-gold/20 animate-float md:block" />
      <HennaHand className="pointer-events-none absolute bottom-10 left-1/3 hidden h-44 w-32 rotate-6 text-gold/25 md:block" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-6 md:grid-cols-12 md:px-10">
        <motion.div style={{ y: textY, opacity: fade }} className="md:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 1 }}
            className="mb-6 font-sans text-xs uppercase tracking-[0.4em] text-gold md:text-sm"
            data-testid="hero-overline"
          >
            Düğünümüze Davetlisiniz
          </motion.p>

          <h1 className="font-serif font-medium leading-[0.82] tracking-tight text-cream">
            <span className="reveal-mask">
              <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block text-6xl md:text-8xl lg:text-[8.5rem]">
                Nurhan
              </motion.span>
            </span>
            <span className="reveal-mask">
              <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block text-5xl italic text-gold md:text-7xl lg:text-[7rem]">
                &amp;
              </motion.span>
            </span>
            <span className="reveal-mask">
              <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block text-6xl md:text-8xl lg:text-[8.5rem]">
                Emre
              </motion.span>
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.2, duration: 1 }}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-sans text-sand"
          >
            <span className="text-lg tracking-wide" data-testid="hero-date">15 Kasım · 19:00</span>
            <span className="hidden h-4 w-px bg-gold/40 md:block" />
            <span className="text-lg tracking-wide">POİ Boğaz Bahçe 2</span>
          </motion.div>
        </motion.div>

        <div className="md:col-span-5">
          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1, duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto aspect-[3/4] w-full max-w-sm"
          >
            <div className="arch-frame h-full w-full border border-gold/30 shadow-2xl shadow-black/50">
              <img
                src="/img/couple.webp"
                alt="Nurhan ve Emre"
                className="h-full w-full object-cover"
                data-testid="hero-couple-image"
              />
            </div>
            <div className="pointer-events-none absolute -inset-3 arch-frame border border-gold/15" />
          </motion.div>
        </div>
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-center"
      >
        <p className="font-sans text-[0.65rem] uppercase tracking-[0.3em] text-sand/70">Kaydırın</p>
        <div className="mx-auto mt-2 h-8 w-px animate-float bg-gradient-to-b from-gold to-transparent" />
      </motion.div>
    </section>
  );
};
